if (!customElements.get("bamboo-fabric-comparison")) {
  customElements.define(
    "bamboo-fabric-comparison",
    class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.controller = new AbortController();
        const on = (target, type, handler, options = {}) =>
          target.addEventListener(type, handler, {
            ...options,
            signal: this.controller.signal,
          });
        this.media = this.querySelector("[data-fabric-media]");
        this.track = this.querySelector("[data-fabric-track]");
        this.slides = [...this.querySelectorAll("[data-fabric-slide]")];
        this.rows = [...this.querySelectorAll("[data-fabric-row]")];
        this.dots = [...this.querySelectorAll("[data-fabric-dot]")];
        this.highlight = this.querySelector("[data-fabric-highlight]");
        this.progress = this.querySelector("[data-fabric-progress]");
        this.fill = this.querySelector("[data-fabric-progress-fill]");
        this.prev = this.querySelector("[data-fabric-prev]");
        this.next = this.querySelector("[data-fabric-next]");
        this.mobile = matchMedia("(max-width: 767px)");
        this.reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
        this.index = 0;
        this.elapsed = 0;
        this.frame = null;
        this.lastTime = null;
        this.visible = false;
        this.hovered = false;
        this.focused = false;
        this.editorSelected = false;
        this.pointer = null;
        this.wheelTime = 0;
        this.wheelDelta = 0;
        this.interval = Number(this.dataset.interval) || 5000;

        this.rows.forEach((row, index) =>
          on(row, "click", () => this.go(index, true)),
        );
        if (this.prev)
          on(this.prev, "click", () => this.go(this.index - 1, true));
        if (this.next)
          on(this.next, "click", () => this.go(this.index + 1, true));
        on(this, "keydown", (event) => {
          if (!event.target.closest("[data-fabric-row], [data-fabric-media]"))
            return;
          const directions = {
            ArrowRight: 1,
            ArrowDown: 1,
            ArrowLeft: -1,
            ArrowUp: -1,
          };
          if (
            !(event.key in directions) &&
            event.key !== "Home" &&
            event.key !== "End"
          )
            return;
          event.preventDefault();
          this.focused = true;
          const index =
            event.key === "Home"
              ? 0
              : event.key === "End"
                ? this.slides.length - 1
                : this.index + directions[event.key];
          this.go(index, true);
          if (event.target.matches("[data-fabric-row]"))
            this.rows[this.index].focus({ preventScroll: true });
        });
        // Disabled: keep playback running while the mouse is over the component.
        // on(this, "pointerenter", (event) => {
        //   if (event.pointerType !== "mouse") return;
        //   this.hovered = true;
        //   this.updatePlayback();
        // });
        // on(this, "pointerleave", () => {
        //   this.hovered = false;
        //   this.updatePlayback();
        // });
        on(this, "pointerdown", () => {
          this.focused = false;
          this.updatePlayback();
        });
        on(this, "focusin", (event) => {
          this.focused = event.target.matches(":focus-visible");
          this.updatePlayback();
        });
        on(this, "focusout", (event) => {
          this.focused =
            this.contains(event.relatedTarget) &&
            event.relatedTarget.matches(":focus-visible");
          this.updatePlayback();
        });
        const startDrag = (id, x) => {
          this.pointer = id;
          this.startX = x;
          this.dx = 0;
          this.media.classList.add("is-dragging");
          this.updatePlayback();
        };
        const moveDrag = (x) => {
          this.dx = x - this.startX;
          const edge =
            (this.index === 0 && this.dx > 0) ||
            (this.index === this.slides.length - 1 && this.dx < 0);
          this.positionTrack(this.dx * (edge ? 0.3 : 1));
        };
        const finishDrag = (commit) => {
          const threshold =
            this.media.clientWidth * (this.mobile.matches ? 0.05 : 0.15);
          const change = commit && Math.abs(this.dx) > threshold;
          const direction = this.dx < 0 ? 1 : -1;
          this.pointer = null;
          this.touchId = null;
          this.touchAxis = null;
          this.media.classList.remove("is-dragging");
          if (change) this.go(this.index + direction, true);
          else {
            this.positionTrack();
            this.updatePlayback();
          }
        };
        // Touch has its own direction lock. A browser pointercancel during
        // scroll arbitration must not reset an in-progress horizontal swipe.
        this.touchId = null;
        on(this.media, "touchstart", (event) => {
          if (event.touches.length !== 1) {
            if (this.touchId !== null) finishDrag(false);
            return;
          }
          if (event.target.closest("button") || this.slides.length < 2) return;
          const touch = event.touches[0];
          this.touchId = touch.identifier;
          this.startY = touch.clientY;
          this.touchAxis = null;
          this.focused = false;
          startDrag(`touch-${touch.identifier}`, touch.clientX);
        }, { passive: true });
        on(this.media, "touchmove", (event) => {
          if (this.touchId === null) return;
          const touch = Array.from(event.touches).find(
            (item) => item.identifier === this.touchId,
          );
          if (!touch) return;
          const dx = touch.clientX - this.startX;
          const dy = touch.clientY - this.startY;
          if (!this.touchAxis) {
            if (Math.max(Math.abs(dx), Math.abs(dy)) < 6) return;
            this.touchAxis = Math.abs(dx) >= Math.abs(dy) ? "x" : "y";
            if (this.touchAxis === "y") {
              finishDrag(false);
              return;
            }
          }
          if (event.cancelable) event.preventDefault();
          moveDrag(touch.clientX);
        }, { passive: false });
        const endTouch = (event) => {
          if (this.touchId === null) return;
          const touch = Array.from(event.changedTouches).find(
            (item) => item.identifier === this.touchId,
          );
          if (!touch) return;
          const commit = event.type === "touchend" && this.touchAxis === "x";
          if (commit) moveDrag(touch.clientX);
          finishDrag(commit);
        };
        on(this.media, "touchend", endTouch, { passive: true });
        on(this.media, "touchcancel", endTouch, { passive: true });
        on(this.media, "pointerdown", (event) => {
          if (
            event.pointerType === "touch" ||
            !event.isPrimary ||
            event.button !== 0 ||
            event.target.closest("button") ||
            this.slides.length < 2
          )
            return;
          startDrag(event.pointerId, event.clientX);
          this.media.setPointerCapture(event.pointerId);
        });
        on(this.media, "pointermove", (event) => {
          if (this.pointer !== event.pointerId) return;
          moveDrag(event.clientX);
        });
        const endDrag = (event) => {
          if (this.pointer !== event.pointerId) return;
          if (event.type === "pointerup") moveDrag(event.clientX);
          finishDrag(event.type === "pointerup");
          if (this.media.hasPointerCapture(event.pointerId))
            this.media.releasePointerCapture(event.pointerId);
        };
        on(this.media, "pointerup", endDrag);
        on(this.media, "pointercancel", endDrag);
        on(this.media, "lostpointercapture", endDrag);
        on(
          this.media,
          "wheel",
          (event) => {
            if (
              this.mobile.matches ||
              Math.abs(event.deltaX) <= Math.abs(event.deltaY)
            )
              return;
            const direction = event.deltaX > 0 ? 1 : -1;
            if (
              this.index + direction < 0 ||
              this.index + direction >= this.slides.length
            )
              return;
            event.preventDefault();
            const now = performance.now();
            if (now < this.wheelTime) return;
            if (
              now - this.lastWheel > 200 ||
              Math.sign(this.wheelDelta) !== direction
            )
              this.wheelDelta = 0;
            this.lastWheel = now;
            this.wheelDelta +=
              event.deltaX *
              (event.deltaMode === 1
                ? 16
                : event.deltaMode === 2
                  ? this.media.clientWidth
                  : 1);
            if (Math.abs(this.wheelDelta) > 40) {
              this.wheelTime = now + 700;
              this.wheelDelta = 0;
              this.go(this.index + direction, true);
            }
          },
          { passive: false },
        );
        on(document, "visibilitychange", () => this.updatePlayback());
        on(this.mobile, "change", () => {
          this.layout();
          this.updatePlayback();
        });
        on(this.reducedMotion, "change", () => this.updatePlayback());
        on(this, "shopify:block:select", (event) => {
          const index = this.rows.findIndex(
            (row) => row.dataset.blockId === event.detail.blockId,
          );
          if (index < 0) return;
          this.editorSelected = true;
          this.go(index);
        });
        on(this, "shopify:block:deselect", () => {
          this.editorSelected = false;
          this.updatePlayback();
        });
        this.resizeObserver = new ResizeObserver(() => this.layout());
        this.resizeObserver.observe(this.media);
        this.rows.forEach((row) => this.resizeObserver.observe(row));
        this.intersectionObserver = new IntersectionObserver((entries) => {
          this.visible = entries[0].isIntersecting;
          this.updatePlayback();
        });
        this.intersectionObserver.observe(this);
        this.go(0);
      }

      positionTrack(dx = 0) {
        this.track.style.transform = `translateX(calc(${-this.index * 100}% + ${dx}px))`;
      }

      layout() {
        if (this.pointer === null) this.positionTrack();
        const row = this.rows[this.index];
        if (!row || !this.highlight) return;
        this.highlight.style.height = `${row.offsetHeight}px`;
        this.highlight.style.transform = `translateY(${row.offsetTop}px)`;
      }

      go(index, manual = false) {
        this.index = Math.max(0, Math.min(index, this.slides.length - 1));
        // A desktop selection restarts playback, even while the mouse stays inside.
        if (manual && !this.mobile.matches) this.hovered = false;
        this.elapsed = 0;
        this.lastTime = null;
        this.rows.forEach((row, index) => {
          const active = index === this.index;
          row.classList.toggle("is-active", active);
          row.setAttribute("aria-selected", String(active));
          row.tabIndex = active ? 0 : -1;
        });
        this.slides.forEach((slide, index) =>
          slide.setAttribute("aria-hidden", String(index !== this.index)),
        );
        this.dots.forEach((dot, index) =>
          dot.classList.toggle("is-active", index === this.index),
        );
        if (this.prev) this.prev.disabled = this.index === 0;
        if (this.next)
          this.next.disabled = this.index === this.slides.length - 1;
        if (this.fill) this.fill.style.transform = "scaleX(0)";
        this.layout();
        this.updatePlayback();
      }

      updatePlayback() {
        const enabled =
          this.dataset.autoplay === "true" &&
          this.slides.length > 1 &&
          !this.reducedMotion.matches;
        if (this.progress) this.progress.hidden = !enabled;
        const playing =
          enabled &&
          this.visible &&
          !document.hidden &&
          !this.hovered &&
          !this.focused &&
          this.pointer === null &&
          !this.editorSelected;
        if (this.frame !== null) cancelAnimationFrame(this.frame);
        this.frame = null;
        this.lastTime = null;
        if (!playing) return;
        const tick = (time) => {
          this.frame = null;
          if (this.lastTime !== null) this.elapsed += time - this.lastTime;
          this.lastTime = time;
          if (this.fill)
            this.fill.style.transform = `scaleX(${Math.min(1, this.elapsed / this.interval)})`;
          if (this.elapsed >= this.interval) {
            this.go((this.index + 1) % this.slides.length);
            return;
          }
          this.frame = requestAnimationFrame(tick);
        };
        this.frame = requestAnimationFrame(tick);
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.controller = null;
        this.resizeObserver?.disconnect();
        this.intersectionObserver?.disconnect();
        if (this.frame !== null) cancelAnimationFrame(this.frame);
      }
    },
  );
}
